/**
 * Noise Legend Configuration
 *
 * Defines the legend configurations for noise choropleth layers
 */

import type { LegendConfig } from '../AnalysisMap.types'
import { NOISE_COLORS, NOISE_DB_RANGES } from '../utils/noiseLayerHelpers'

/**
 * Format dB values for legend labels
 */
function formatDbValue(value: number): string {
  return `${value} dB`
}

/**
 * Create legend entries from values and colors with range labels
 */
function createLegendEntries(
  values: number[],
  colors: string[],
  _formatter: (value: number) => string
) {
  return values.slice(0, -1).map((value, index) => {
    const nextValue = values[index + 1]
    const label = nextValue ? `${value} - ${nextValue} dB` : `${value}+ dB`
    return {
      color: colors[index],
      label,
    }
  })
}

/**
 * Noise legend configurations
 */
export const NOISE_LEGEND_CONFIG: Record<string, LegendConfig> = {
  'noise-road-day': {
    title: 'Road Noise - Day',
    type: 'gradient',
    unit: 'dB',
    entries: createLegendEntries(NOISE_DB_RANGES, NOISE_COLORS, formatDbValue),
  },

  'noise-road-night': {
    title: 'Road Noise - Night',
    type: 'gradient',
    unit: 'dB',
    entries: createLegendEntries(NOISE_DB_RANGES, NOISE_COLORS, formatDbValue),
  },

  'noise-railway-day': {
    title: 'Railway Noise - Day',
    type: 'gradient',
    unit: 'dB',
    entries: createLegendEntries(NOISE_DB_RANGES, NOISE_COLORS, formatDbValue),
  },

  'noise-railway-night': {
    title: 'Railway Noise - Night',
    type: 'gradient',
    unit: 'dB',
    entries: createLegendEntries(NOISE_DB_RANGES, NOISE_COLORS, formatDbValue),
  },
}

/**
 * Get legend config for a noise layer
 */
export function getNoiseLegendConfig(
  layerId: string
): LegendConfig | undefined {
  return NOISE_LEGEND_CONFIG[layerId]
}

/**
 * Check if a layer has a noise legend
 */
export function hasNoiseLegend(layerId: string): boolean {
  return layerId in NOISE_LEGEND_CONFIG
}
